import { AdminVideo } from '../model/dto/video/admin-video';
import { VideoPlacement } from '../model/dto/video/video-placement';
import {
  NO_PLACEMENT,
  PLACEMENT_DESCRIPTION_MAX_LENGTH,
  PLACEMENT_KEY_MAX_LENGTH,
  PLACEMENT_KEY_PATTERN,
} from '../model/dto/video/video-constants';
import {
  CreateVideoRequest,
  UpdateVideoRequest,
} from '../model/dto/video/video-requests';
import { HealthGoalType } from '../model/dto/health-goal-type.enum';
import {
  DeleteConfirmation,
  DeleteUploadState,
  PlacementDescriptionError,
  PlacementKeyError,
  PlacementNotice,
  PlacementPickerOption,
  PlacementVisibility,
  PreviewAudience,
  VideoPlacementValues,
} from '../model/type/video-placement.types';
import { VideoUploadItem } from '../../context/video-upload.types';
import { getVideoStatusDisplay, isArchived } from './video-status-utils';

export const HEALTH_GOALS: HealthGoalType[] = Object.values(HealthGoalType);

export const HEALTH_GOAL_LABEL_KEYS: Record<HealthGoalType, string> = {
  [HealthGoalType.TRYING_TO_CONCEIVE]: 'common.userFields.goals.tryToConceive',
  [HealthGoalType.PERIMENOPAUSE_TRACKING]:
    'common.userFields.goals.perimenopauseTracking',
  [HealthGoalType.HORMONE_OPTIMIZATION]:
    'common.userFields.goals.hormoneOptimization',
};

export const isKnownHealthGoal = (goal: string): goal is HealthGoalType =>
  (HEALTH_GOALS as string[]).includes(goal);

const matchesAudience = (
  healthGoals: string[],
  audience: PreviewAudience
): boolean => {
  switch (audience.kind) {
    case 'everyone':
      return true;

    case 'goal':
      return !healthGoals.length || healthGoals.includes(audience.goal);

    case 'noGoal':
      return !healthGoals.length;

    default: {
      const unhandledAudience: never = audience;

      return unhandledAudience;
    }
  }
};

export const PREVIEW_NO_GOAL = 'noGoal';

export const toPreviewAudience = (
  value: string | null | undefined
): PreviewAudience => {
  if (!value) {
    return { kind: 'everyone' };
  }

  return value === PREVIEW_NO_GOAL
    ? { kind: 'noGoal' }
    : { kind: 'goal', goal: value };
};

export const toPreviewParam = (
  audience: PreviewAudience
): string | undefined => {
  switch (audience.kind) {
    case 'everyone':
      return undefined;

    case 'noGoal':
      return PREVIEW_NO_GOAL;

    case 'goal':
      return audience.goal;

    default: {
      const unhandledAudience: never = audience;

      return unhandledAudience;
    }
  }
};

export const getUnknownHealthGoals = (
  videos: AdminVideo[],
  audience: PreviewAudience
): string[] => {
  const goals = videos.flatMap(({ health_goals }) => health_goals);
  const previewed = audience.kind === 'goal' ? [audience.goal] : [];

  return Array.from(new Set([...goals, ...previewed])).filter(
    (goal) => !isKnownHealthGoal(goal)
  );
};

export const isSeenBy = (
  video: AdminVideo,
  audience: PreviewAudience
): boolean =>
  video.status === 'ready' &&
  !isArchived(video) &&
  matchesAudience(video.health_goals, audience);

export const findPlacement = (
  placements: VideoPlacement[],
  key: string | null
): VideoPlacement | undefined =>
  placements.find((placement) => placement.key === key);

export const toPlacementPickerValue = (
  placements: VideoPlacement[] | undefined,
  value: string
): PlacementPickerOption => {
  if (value === NO_PLACEMENT) {
    return { kind: 'none' };
  }

  const placement = findPlacement(placements ?? [], value);

  return placement
    ? {
        kind: 'placement',
        key: placement.key,
        description: placement.description,
      }
    : { kind: 'unlisted', key: value, isRetired: !!placements };
};

export const getPlacementPickerOptions = (
  placements: VideoPlacement[] | undefined,
  value: string,
  canAdd: boolean
): PlacementPickerOption[] => {
  const selected = toPlacementPickerValue(placements, value);

  return [
    { kind: 'none' },
    ...(selected.kind === 'unlisted' ? [selected] : []),
    ...(placements ?? []).map(
      ({ key, description }): PlacementPickerOption => ({
        kind: 'placement',
        key,
        description,
      })
    ),
    ...(canAdd && placements ? [{ kind: 'add' } as const] : []),
  ];
};

export const getPlacementPickerOptionKey = (
  option: PlacementPickerOption
): string => {
  switch (option.kind) {
    case 'none':
      return NO_PLACEMENT;

    case 'placement':
    case 'unlisted':
      return option.key;

    case 'add':
      return '#add';

    default: {
      const unhandledOption: never = option;

      return unhandledOption;
    }
  }
};

export const matchesPlacementSearch = (
  option: PlacementPickerOption,
  search: string
): boolean => {
  const query = search.trim().toLowerCase();

  if (!query || option.kind !== 'placement') {
    return true;
  }

  return (
    option.description.toLowerCase().includes(query) ||
    option.key.toLowerCase().includes(query)
  );
};

export const placementKeyError = (
  key: string,
  placements: VideoPlacement[]
): PlacementKeyError | null => {
  if (!key) {
    return 'required';
  }

  if (key.length > PLACEMENT_KEY_MAX_LENGTH) {
    return 'tooLong';
  }

  if (!PLACEMENT_KEY_PATTERN.test(key)) {
    return 'pattern';
  }

  if (key === NO_PLACEMENT) {
    return 'reserved';
  }

  return findPlacement(placements, key) ? 'exists' : null;
};

export const placementDescriptionError = (
  description: string
): PlacementDescriptionError | null => {
  const trimmed = description.trim();

  if (!trimmed) {
    return 'required';
  }

  return trimmed.length > PLACEMENT_DESCRIPTION_MAX_LENGTH ? 'tooLong' : null;
};

export const getPlacementNotice = (
  video: AdminVideo,
  upload?: VideoUploadItem
): PlacementNotice | null => {
  if (!video.placement_key) {
    return null;
  }

  if (isArchived(video)) {
    return 'archived';
  }

  const display = getVideoStatusDisplay(video, upload);

  switch (display.kind) {
    case 'status':
      switch (display.status) {
        case 'ready':
          return null;

        case 'failed':
          return 'failed';

        case 'uploading':
        case 'processing':
          return 'pending';

        default: {
          const unhandledStatus: never = display.status;

          return unhandledStatus;
        }
      }

    case 'queued':
    case 'uploading':
    case 'finishing':
      return 'pending';

    case 'uploadFailed':
    case 'uploadNotFinished':
      return 'uploadStopped';

    default: {
      const unhandledDisplay: never = display;

      return unhandledDisplay;
    }
  }
};

export const isValidPosition = (position: string): boolean =>
  /^\d+$/.test(position.trim());

export const isPlacementPositionValid = ({
  placement_key,
  position,
}: VideoPlacementValues): boolean =>
  placement_key === NO_PLACEMENT || isValidPosition(position);

export const getPlacementVisibility = ({
  placement_key,
  health_goals,
}: VideoPlacementValues): PlacementVisibility => {
  if (placement_key === NO_PLACEMENT) {
    return { kind: 'notShown' };
  }

  return health_goals.length
    ? { kind: 'goals', goals: health_goals }
    : { kind: 'everyone' };
};

export const NEW_PLACEMENT_VALUES: VideoPlacementValues = {
  placement_key: NO_PLACEMENT,
  position: '0',
  health_goals: [],
};

export const toCreatePlacementFields = ({
  placement_key,
  position,
  health_goals,
}: VideoPlacementValues): Pick<
  CreateVideoRequest,
  'placement_key' | 'position' | 'health_goals'
> =>
  placement_key === NO_PLACEMENT
    ? {}
    : { placement_key, position: Number(position.trim()), health_goals };

const sameGoals = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((goal) => b.includes(goal));

export const toPlacementValues = (video: AdminVideo): VideoPlacementValues => ({
  placement_key: video.placement_key ?? NO_PLACEMENT,
  position: String(video.position),
  health_goals: video.health_goals,
});

export const toPlacementUpdate = (
  video: AdminVideo,
  values: VideoPlacementValues
): UpdateVideoRequest => {
  const placementKey =
    values.placement_key === NO_PLACEMENT ? null : values.placement_key;
  const keyChange =
    placementKey !== video.placement_key ? { placement_key: placementKey } : {};

  if (!placementKey) {
    return keyChange;
  }

  const position = Number(values.position.trim());

  return {
    ...keyChange,
    ...(position !== video.position ? { position } : {}),
    ...(sameGoals(values.health_goals, video.health_goals)
      ? {}
      : { health_goals: values.health_goals }),
  };
};

const getDeleteUploadState = (
  video: AdminVideo,
  upload?: VideoUploadItem
): DeleteUploadState => {
  const display = getVideoStatusDisplay(video, upload);

  switch (display.kind) {
    case 'uploadNotFinished':
      return 'notFinished';
    case 'queued':
    case 'uploading':
    case 'finishing':
    case 'uploadFailed':
      return 'local';
    case 'status':
      return 'none';
    default: {
      const unreachable: never = display;

      return unreachable;
    }
  }
};

export const getDeleteConfirmation = (
  video: AdminVideo,
  placements: VideoPlacement[],
  upload?: VideoUploadItem
): DeleteConfirmation => {
  const description = video.placement_key
    ? findPlacement(placements, video.placement_key)?.description
    : undefined;
  const isLive = !isArchived(video);

  return {
    action: video.hls_url ? 'archive' : 'delete',
    placement: !video.placement_key
      ? null
      : description
      ? { kind: 'described', description }
      : { kind: 'unavailable', key: video.placement_key },
    isPendingReplacement:
      !!video.replaces_video_id && video.status !== 'ready' && isLive,
    hasPendingReplacement: !!video.replaced_by_video_id && isLive,
    upload: getDeleteUploadState(video, upload),
  };
};

export const isSameDeleteConfirmation = (
  first: DeleteConfirmation,
  second: DeleteConfirmation
): boolean => JSON.stringify(first) === JSON.stringify(second);

export const isOrderEditable = (video: AdminVideo): boolean =>
  !!video.placement_key && !isArchived(video);
